import { useEffect, useState, useRef } from 'react';

import { WsFlight } from 'types/flights';
import { eventEmitter, wsService } from '../../../index';
import { tokenManager } from 'core/redux/api/authorization';
import { EventEmitterMessages } from 'constants/event-emitter';

export function useFlightInfoWidget(uri?: string | undefined, categoryIds?: number[]) {
  const [flight, setFlight] = useState<WsFlight | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const currentFlightKey = useRef<string | null>(null);

  useEffect(() => {
    if (!uri) {
      setFlight(null);
      return;
    }

    const aircraftCategory = process.env.REACT_APP_CATEGORY_ID ? Number(process.env.REACT_APP_CATEGORY_ID) : undefined;
    const belongsToCategory = !!aircraftCategory && categoryIds?.includes(aircraftCategory);

    if (!belongsToCategory) {
      setFlight(null);
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;

    (async () => {
      setLoading(true);

      try {
        const token = tokenManager.getAccessToken();
        const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/v3/veta/flight/${uri}/leg`, {
          headers: { Authorization: `Bearer ${token.jwt}` },
          signal,
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data: WsFlight = await res.json();

        currentFlightKey.current = `${data.routeLeg.airline}_${data.routeLeg.flightNumber}`;

        setFlight(data);
      } catch (e) {
        if (e.name !== 'AbortError') setError(e);
        setFlight(null);
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      controller.abort();
    };
  }, [uri, categoryIds]);

  useEffect(() => {
    if (!uri) return;

    const aircraftCategory = process.env.REACT_APP_CATEGORY_ID ? Number(process.env.REACT_APP_CATEGORY_ID) : undefined;
    const belongsToCategory = !!aircraftCategory && categoryIds?.includes(aircraftCategory);

    if (!belongsToCategory) return;

    const handler = (wsFlight: WsFlight) => {
      const key = `${wsFlight.routeLeg.airline}_${wsFlight.routeLeg.flightNumber}`;

      if (key === currentFlightKey.current) {
        setFlight(wsFlight);
      }
    };

    eventEmitter.on(EventEmitterMessages.FlightUpdate, handler);

    wsService.subscribeToFlight(uri);

    return () => {
      wsService.unsubscribeToFlight(uri);
      eventEmitter.removeListener(EventEmitterMessages.FlightUpdate, handler);
    };
  }, [uri, categoryIds]);

  return { flight, loading, error };
}
